/**
 * 无限滚动Hook
 * 使用 IntersectionObserver API 实现滚动到底部自动加载
 * 
 * 工作原理：
 * 1. 监听一个目标元素（通常是一个底部的触发器div）
 * 2. 当目标元素进入可视区域时，触发加载更多
 * 3. 性能优于传统的scroll事件监听
 */

import { useEffect, useCallback, useState, useRef } from 'react'

// Hook参数接口
interface UseInfiniteScrollOptions {
  hasMore: boolean              // 是否还有更多数据（false时停止监听）
  onLoadMore: () => Promise<unknown> | void // 加载更多的回调函数
  threshold?: number            // 触发距离（像素，暂未使用）
  rootMargin?: string           // 提前触发的距离（IntersectionObserver参数）
}

export const useInfiniteScroll = ({
  hasMore,
  onLoadMore,
//   threshold = 100,               // 默认100px
  rootMargin = '100px'           // 默认提前100px触发
}: UseInfiniteScrollOptions) => {
  // 内部加载状态（防止重复触发）
  const [loading, setLoading] = useState(false)
  
  // IntersectionObserver实例引用
  const observerRef = useRef<IntersectionObserver | null>(null)
  
  // 监听目标元素的ref
  const targetRef = useRef<HTMLDivElement | null>(null)

  /**
   * 处理加载更多逻辑
   * 加载中或没有更多数据时不执行
   */
  const handleLoadMore = useCallback(async () => {
    if (loading || !hasMore) return

    setLoading(true)
    try {
      await onLoadMore()
    } finally {
      setLoading(false)
    }
  }, [loading, hasMore, onLoadMore])

  /**
   * 设置IntersectionObserver监听
   * 依赖hasMore和handleLoadMore，当它们变化时重新设置
   */
  useEffect(() => {
    // 如果没有目标元素或没有更多数据，不设置监听
    if (!targetRef.current || !hasMore) return

    // 创建IntersectionObserver实例
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        // 当目标元素进入可视区域时触发加载
        if (entry.isIntersecting) {
          handleLoadMore()
        }
      },
      { 
        rootMargin  // 提前触发距离，例如 '100px' 表示距离可视区域底部100px时触发
      }
    )

    // 开始观察目标元素
    observerRef.current.observe(targetRef.current)

    // 清理函数：组件卸载时断开观察
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [hasMore, handleLoadMore, rootMargin])

  return {
    loading,      // 当前是否正在加载
    targetRef,    // 需要绑定到触发元素上的ref
  }
}
